import EnvService from './EnvService'
import FlatfileImporter from 'flatfile-csv-importer'
import { apolloClient } from '../apollo'
import { authUserQuery } from '../graphql/auth'
import moment from 'moment'

/**
 * Create an instance of the Flatfile importer
 * @param options
 * @returns {Promise<FlatfileImporter>}
 */
export const flatfileImporter = async (options) => {
  // get the Flatfile License Key from our environment
  const licenseKey = EnvService.getEnv('FLATFILE_KEY')

  // set our Flatfile version to the newest version
  FlatfileImporter.setVersion(2)

  // create an instance of the Importer
  const importer = new FlatfileImporter(licenseKey, {
    ...options || {},
    styleOverrides: {
      primaryButtonColor: '#0077FF'
    },
    maxRecords: 10000,
    allowCustom: false,
    allowInvalidSubmit: false
  })

  // identify with the user data
  const user = await apolloClient.query({
    query: authUserQuery,
    fetchPolicy: 'network-only'
  })

  // set the customer identity
  importer.setCustomer({
    userId: String(user.id),
    name: [user.first_name || '', user.last_name || ''].join(' ').trim(),
    email: user.email,
    companyId: user.campaign_id
  })

  return importer
}

/**
 * Validate email addresses
 * @type {{regex: string, error: string, validate: string}}
 */
export const emailValidator = {
  validate: 'regex_matches',
  // eslint-disable-next-line no-useless-escape
  regex: `^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$`,
  error: 'Must an email address (hint: check for extra spaces at the beginning/end)'
}

/**
 * Validate boolean values
 * @type {{regex: string, regexFlags: {ignoreCase: boolean}, error: string, validate: string}}
 */
export const booleanValidator = {
  validate: 'regex_matches',
  regex: '^(?:1|true|0|false)$',
  regexFlags: { ignoreCase: true },
  error: 'Value must be one of these: TRUE, FALSE, 0, 1'
}

/**
 * Validate number/decimal
 * @type {{regex: string, error: string, validate: string}}
 */
export const decimalValidator = {
  validate: 'regex_matches',
  // eslint-disable-next-line no-useless-escape
  regex: '^(0|[1-9]\\d*)(\\.\\d+)?$',
  error: 'Must only contain numbers and decimal points'
}

/**
 * Validate Phone Type
 * @type {{regex: string, regexFlags: {ignoreCase: boolean}, error: string, validate: string}}
 */
export const contactPhoneTypeValidator = {
  validate: 'regex_matches',
  regex: '^(MOBILE|HOME|WORK|OTHER)$',
  regexFlags: { ignoreCase: true },
  error: 'Value must be one of these: MOBILE, HOME, WORK, OTHER'
}

/**
 * Validate Email Type
 * @type {{regex: string, regexFlags: {ignoreCase: boolean}, error: string, validate: string}}
 */
export const contactEmailTypeValidator = {
  validate: 'regex_matches',
  regex: '^(HOME|WORK|OTHER)$',
  regexFlags: { ignoreCase: true },
  error: 'Value must be one of these: HOME, WORK, OTHER'
}

/**
 * A hook to check that the names match the record type
 * @param importer
 */
export const addNameFieldsHook = (importer) => {
  importer.registerRecordHook((record) => {
    const out = {}
    // if this is an organization, does it have a name?
    const orgName = (record['organizationName'] || '').trim()
    const isOrg = isTruthy(record['isOrganization'])
    // if this is an organization, check that it has a name
    if (isOrg && !orgName) {
      out['organizationName'] = {
        info: [{
          message: 'Organizations must specify a name',
          level: 'error'
        }]
      }
    }
    // if this is not an organization, then it must not have an organization name
    if (!isOrg && orgName) {
      out['organizationName'] = {
        info: [{
          message: 'Only organizations can have an organization name. If this is an organization, then set the "Contact is Organization" column to TRUE. If this is an individual, you may be looking for the "Employer" column.',
          level: 'error'
        }]
      }
      out['isOrganization'] = {
        info: [{
          message: 'If this is an organization, set this field to TRUE',
          level: 'error'
        }]
      }
    }
    // if this is an individual, then it must have at least a first name
    const firstName = (record['firstName'] || '').trim()
    const fullName = (record['fullName'] || '').trim()
    if (!isOrg && !orgName && !firstName && !fullName) {
      out['firstName'] = {
        info: [{
          message: 'Contact must have at least a First Name, or an Organization Name if this is an organization.',
          level: 'error'
        }]
      }
      out['fullName'] = {
        info: [{
          message: 'Contact must have at least a First Name, or an Organization Name if this is an organization.',
          level: 'error'
        }]
      }
    }
    return out
  })
}

/**
 * A hook to validate date formats
 * @param importer
 * @param fieldName
 * @param dateFormat
 */
export const addDateFieldHook = (importer, fieldName, dateFormat = 'YYYY-MM-DD') => {
  importer.registerFieldHook(fieldName, values => {
    const out = []
    // validate any date values
    values.forEach(value => {
      // extract the value and the row number
      let [val, rowId] = value
      // skip empty values
      val = typeof val === 'string' ? val.trim() : val
      if (!val) {
        return
      }
      // handle invalid dates
      const dateMoment = moment(val)
      if (!dateMoment.isValid()) {
        out.push([
          {
            info: [{
              message: 'Must be a valid date',
              level: 'error'
            }]
          },
          rowId
        ])
        return
      }
      // otherwise, reformat the date
      out.push([
        {
          info: [{
            message: 'Reformatted into YYYY-MM-DD format.',
            level: 'info'
          }],
          value: dateMoment.format(dateFormat)
        },
        rowId
      ])
    })
    return out
  })
}

export const addTrimRecordHook = (importer) => {
  importer.registerRecordHook((record, index) => {
    const out = {}
    // trim the values
    Object.keys(record).forEach(key => {
      const value = record[key]
      if (value && typeof value === 'string') {
        let trimmedValue = value.trim()
        if (value !== trimmedValue) {
          out[key] = {
            value: value.trim(),
            info: [
              {
                message: 'Extra spaces removed.',
                level: 'info'
              }
            ]
          }
        }
      }
    })
    return out
  })
}

export const isTruthy = (val) => {
  val = typeof val === 'string' ? val.toLowerCase() : val
  return [true, 'true', 1].includes(val)
}

export const isFalsy = (val) => {
  val = typeof val === 'string' ? val.toLowerCase() : val
  return [false, 'false', 0].includes(val)
}
